import { CHAPTER_CARD } from '@/render/palette';
import { BEATS } from '@/story/beats';
import type { PlaythroughTimeline } from './timeline';

/**
 * The hidden panel: jump to a beat, and read the stopwatch.
 *
 * ── Why it is in the shipped build, said out loud ───────────────────────────
 *
 * Spec §10's definition of done includes *"no debug UI in the shipped build"*,
 * and this is debug UI in the shipped build. **That is a decision (D-125), not
 * an oversight.** Alex reviews this game by opening the deployed link on a
 * phone. A debug affordance that only exists behind a dev flag, or only responds
 * to a keyboard chord, is an affordance for the machine that already has an
 * address bar — it would make the project's top task cheaper for nobody.
 *
 * Removal before release is on the checklist in `docs/backlog.md`. The reason it
 * can wait is that the cost of shipping it early is one screen nobody finds, and
 * the cost of not shipping it is the top task staying undone for a twelfth
 * session.
 *
 * ── Why the gesture is a corner tap and not a key ───────────────────────────
 *
 * Same reason. Four taps in the top-left corner inside two seconds: nothing a
 * player does by accident, nothing a player discovers by exploring, and
 * reachable with one thumb on a phone held in one hand. The backquote key does
 * the same thing on a desktop, because it costs one line.
 *
 * ── Why jumping is a navigation ─────────────────────────────────────────────
 *
 * Because it is the same operation as Continue and as the hand-over between
 * beats, and there is exactly one of those (D-126). `docs/backlog.md` called
 * this before it was built: *"a second, in-place path that tears down and
 * rebuilds the world without a reload would be a different code path that only
 * the debug menu exercises, and this project's standing failure is two
 * derivations of one thing."*
 */

function colour(value: number, alpha = 1): string {
  const red = (value >> 16) & 0xff;
  const green = (value >> 8) & 0xff;
  const blue = value & 0xff;
  return `rgb(${red} ${green} ${blue} / ${alpha})`;
}

const INK = colour(CHAPTER_CARD.ink);
const GROUND = colour(CHAPTER_CARD.ground, 0.97);

/** Taps needed, and the window they must land in. */
const TAP_COUNT = 4;
const TAP_WINDOW_SECONDS = 2;
/** The corner, in CSS pixels from the top-left. */
const CORNER_PX = 72;

export const DEV_PANEL_ID = 'rosalie-dev-panel';

export interface DevPanelOptions {
  /** The beat currently running, so the panel can mark it. */
  readonly beat: number;
  /** The one-based room part currently running, so multipart jumps stay honest. */
  readonly part: number;
  /** The stopwatch for this run. Read when the panel opens, never held open. */
  readonly timeline: PlaythroughTimeline;
}

/**
 * Arm the gesture. Returns a disposer.
 *
 * The panel itself is built on open and destroyed on close: it is read a handful
 * of times in a session, and a live DOM tree updating behind a closed overlay is
 * a frame cost and a source of stale state for no benefit.
 */
export function armDevPanel(options: DevPanelOptions): () => void {
  let taps: number[] = [];
  let open = false;

  const onPointerDown = (event: PointerEvent): void => {
    if (event.clientX > CORNER_PX || event.clientY > CORNER_PX) return;
    const now = performance.now() / 1000;
    taps = [...taps.filter((at) => now - at <= TAP_WINDOW_SECONDS), now];
    if (taps.length < TAP_COUNT) return;
    taps = [];
    toggle();
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== '`') return;
    toggle();
  };

  function toggle(): void {
    if (open) {
      close();
      return;
    }
    open = true;
    document.body.append(panel(options, close));
  }

  function close(): void {
    open = false;
    document.getElementById(DEV_PANEL_ID)?.remove();
  }

  window.addEventListener('pointerdown', onPointerDown);
  window.addEventListener('keydown', onKeyDown);

  return () => {
    window.removeEventListener('pointerdown', onPointerDown);
    window.removeEventListener('keydown', onKeyDown);
    close();
  };
}

function panel(options: DevPanelOptions, close: () => void): HTMLDivElement {
  const root = document.createElement('div');
  root.id = DEV_PANEL_ID;
  root.style.cssText = [
    'position:fixed',
    'inset:0',
    // Above the pause overlay (30) and the shell (40): this is the last thing
    // anybody opens and it must be reachable from a paused game.
    'z-index:60',
    'display:flex',
    'flex-direction:column',
    'gap:1rem',
    'overflow:auto',
    'padding:calc(env(safe-area-inset-top) + 1.25rem) 1.25rem calc(env(safe-area-inset-bottom) + 1.25rem)',
    `background:${GROUND}`,
    `color:${INK}`,
    'font:13px/1.5 ui-monospace,SFMono-Regular,Menlo,monospace',
  ].join(';');

  const beats = document.createElement('div');
  beats.style.cssText = 'display:flex;flex-wrap:wrap;gap:0.5rem';
  for (const [number, entry] of Object.entries(BEATS)) {
    for (const [index] of entry.parts.entries()) {
      const part = index + 1;
      const jump = document.createElement('button');
      jump.type = 'button';
      jump.textContent = entry.parts.length === 1
        ? `${number} · ${entry.title}`
        : `${number}.${part} · ${entry.title}`;
      const here = Number(number) === options.beat && part === options.part;
      // The original one-room jumps are a public debug seam used by phone
      // review automation. Multipart beats need distinct targets, but changing
      // every older id would break that seam for no product benefit.
      jump.dataset['jump'] = entry.parts.length === 1 ? number : `${number}.${part}`;
      if (here) jump.setAttribute('aria-current', 'page');
      jump.style.cssText = [
        'appearance:none',
        'padding:0.5rem 0.75rem',
        `border:1px solid ${colour(CHAPTER_CARD.ink, here ? 0.9 : 0.4)}`,
        `background:${here ? colour(CHAPTER_CARD.ink, 0.16) : 'transparent'}`,
        `color:${INK}`,
        'border-radius:0',
        'font:inherit',
        'cursor:pointer',
      ].join(';');
      jump.addEventListener('click', () => {
        const suffix = part === 1 ? '' : `&part=${part}`;
        window.location.replace(`${window.location.pathname}?beat=${number}${suffix}`);
      });
      beats.append(jump);
    }
  }

  const stopwatch = document.createElement('pre');
  stopwatch.textContent = options.timeline.text;
  stopwatch.style.cssText = [
    'margin:0',
    'white-space:pre',
    'overflow-x:auto',
    'padding:0.75rem',
    `border:1px solid ${colour(CHAPTER_CARD.ink, 0.3)}`,
    'font:inherit',
    // Selectable, unlike the rest of the game's chrome: copying by hand is the
    // fallback when the clipboard API is refused, which it is on any page the
    // browser does not consider a secure context.
    '-webkit-user-select:text',
    'user-select:text',
  ].join(';');

  const actions = document.createElement('div');
  actions.style.cssText = 'display:flex;gap:0.5rem;flex-wrap:wrap';

  const copy = document.createElement('button');
  copy.type = 'button';
  copy.textContent = 'Copy timings';
  copy.addEventListener('click', () => {
    void navigator.clipboard?.writeText(options.timeline.text).then(
      () => {
        copy.textContent = 'Copied';
      },
      () => {
        // No console line: `no-console` is an error here and the failure has a
        // better home than a log nobody has open on a phone — the text is
        // selectable, and saying so is more use than saying it went wrong.
        copy.textContent = 'Select the text above to copy';
      },
    );
  });

  const dismiss = document.createElement('button');
  dismiss.type = 'button';
  dismiss.textContent = 'Close';
  dismiss.addEventListener('click', close);

  for (const control of [copy, dismiss]) {
    control.style.cssText = [
      'appearance:none',
      'padding:0.5rem 0.9rem',
      `border:1px solid ${colour(CHAPTER_CARD.ink, 0.4)}`,
      'background:transparent',
      `color:${INK}`,
      'border-radius:0',
      'font:inherit',
      'cursor:pointer',
    ].join(';');
  }
  actions.append(copy, dismiss);

  root.append(beats, stopwatch, actions);
  return root;
}
